import { Typography, Image, FlexGrid, Spacer } from '@telus-uds/components-web'
import useViewport from 'src/hooks/useBBViewport'
import { BlockRenderer } from 'src/siteBuilder/renderer/contentful/BlockRenderer'
import { TypeBxTextOverImageHeroWithoutUnresolvableLinksResponse } from 'contentful-types'

import styled from 'styled-components'

const HeroImageWrapper = styled.div<{ heightOverride: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${(props) => (props?.heightOverride ? `height: ${props.heightOverride}px;` : `height: 600px;`)}
  box-sizing: border-box;
  > img {
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
    object-fit: cover;
    object-position: center;
  }
`

const ContentWrapper = styled.div<{ mainContentOverride?: boolean; heightOverride: number }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  padding: 0px 32px;
  box-sizing: border-box;
  z-index: 99;
  width: 100%;
  ${(props) => (props.mainContentOverride ? 'justify-content: start;' : 'justify-content: space-evenly;')};
  height: ${(props) => (props.heightOverride ? `${props.heightOverride}px;` : `100%;`)}
  max-width: 1200px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
`

const BXTextOverImageHero = ({
  mainContentText = '',
  heroImage,
  bottomLeftLogo,
  bottomRightText,
  mainContentOverride,
  mainContextFontSizeXs = 60,
  mainContextFontSizeSm = 60,
  mainContextFontSizeMd = 60,
  mainContextFontSizeLg = 60,
  mainContextFontSizeXl = 60,
  heightOverride,
}: TypeBxTextOverImageHeroWithoutUnresolvableLinksResponse['fields']) => {
  const viewport = useViewport()

  const fontSizes = {
    xl: mainContextFontSizeXl,
    lg: mainContextFontSizeLg,
    md: mainContextFontSizeMd,
    sm: mainContextFontSizeSm,
    xs: mainContextFontSizeXs,
  }

  const calculatedFontsize = fontSizes[viewport]

  const formattedMainText = mainContentText.replaceAll(' ', '\n')

  const isMobileViewport = viewport === 'xs' || viewport === 'sm'

  const calculatedHeight = heightOverride ? heightOverride : 600

  return (
    <HeroImageWrapper heightOverride={heightOverride} data-testid="hero-image-wrapper">
      <Image
        src={heroImage?.fields?.file?.url}
        alt={heroImage?.fields?.description}
        width={heroImage?.fields?.file?.details?.image?.width}
        height={calculatedHeight}
        data-testid="hero-image"
      />
      <ContentWrapper
        mainContentOverride={!!mainContentOverride}
        heightOverride={heightOverride}
        data-testid="hero-content-wrapper"
      >
        {mainContentOverride ? (
          <BlockRenderer data-testid={'hero-over-text-override'} block={mainContentOverride} />
        ) : (
          <Typography
            block
            variant={{ colour: 'white', size: 'display1' }}
            heading="h2"
            tokens={{
              lineHeight: 1,
              fontSize: calculatedFontsize,
              fontWeight: 700,
            }}
          >
            {formattedMainText}
          </Typography>
        )}

        {!mainContentOverride ? (
          <FlexGrid.Row verticalAlign="middle" distribute="between">
            {bottomLeftLogo && (
              <FlexGrid.Col sm={12} md={3}>
                <Image src={bottomLeftLogo?.fields?.file?.url} alt={bottomLeftLogo?.fields?.description} width={200} />
                {isMobileViewport ? <Spacer space={2} /> : null}
              </FlexGrid.Col>
            )}

            {bottomRightText && (
              <FlexGrid.Col sm={12} md={8} lg={8} horizontalAlign={isMobileViewport ? 'start' : 'end'}>
                <Typography
                  align="left"
                  variant={{ colour: 'white', size: 'display2' }}
                  heading="h1"
                  tokens={{
                    fontWeight: 300,
                  }}
                >
                  {bottomRightText}
                </Typography>
              </FlexGrid.Col>
            )}
          </FlexGrid.Row>
        ) : null}
      </ContentWrapper>
    </HeroImageWrapper>
  )
}

export default BXTextOverImageHero
